import {
  AccountAnalysisConfiguration,
  AnalysisEntity,
  AnalysisProfile
} from "../../features/account-analysis/types";
import { supabase } from "../supabase/client";

// Analysis Entities
// NOTE(types): DB stores `status` as generic `string`; we narrow it to the app-level
// union here since inserts/updates only ever write "active" or "disabled".
export const fetchAnalysisEntities = async (): Promise<AnalysisEntity[]> => {
  const { data, error } = await supabase
    .from("analysis_entity")
    .select("*")
    .is("deleted", null)
    .order("created", { ascending: false });

  if (error) {
    throw error;
  }
  return (data || []) as AnalysisEntity[];
};

export const createAnalysisEntity = async (
  entity: Omit<AnalysisEntity, "id" | "created" | "modified" | "deleted">
): Promise<AnalysisEntity> => {
  const { data, error } = await supabase
    .from("analysis_entity")
    .insert(entity)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as AnalysisEntity;
};

export const updateAnalysisEntity = async (
  id: string,
  updates: Partial<AnalysisEntity>
): Promise<AnalysisEntity> => {
  const { data, error } = await supabase
    .from("analysis_entity")
    .update({ ...updates, modified: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as AnalysisEntity;
};

export const deleteAnalysisEntity = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from("analysis_entity")
    .update({ deleted: new Date().toISOString() })
    .eq("id", id);

  if (error) {
    throw error;
  }
};

export const fetchAllAnalysisEntityEmails = async (): Promise<{ email: string; is_deleted: boolean }[]> => {
  const { data, error } = await supabase
    .from("analysis_entity")
    .select("email, deleted")
    .order("created", { ascending: false });

  if (error) {
    throw error;
  }
  return (data || []).map(item => ({
    email: item.email,
    is_deleted: item.deleted !== null
  }));
};

export const upsertAnalysisEntity = async (
  entity: Omit<AnalysisEntity, "id" | "created" | "modified" | "deleted">
): Promise<AnalysisEntity> => {
  const { data, error } = await supabase
    .from("analysis_entity")
    .upsert(
      {
        ...entity,
        deleted: null,
        modified: new Date().toISOString()
      },
      {
        onConflict: "tenant_id,email",
        ignoreDuplicates: false
      }
    )
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as AnalysisEntity;
};

export const upsertAnalysisEntitiesBulk = async (
  entities: Omit<AnalysisEntity, "id" | "created" | "modified" | "deleted">[]
): Promise<AnalysisEntity[]> => {
  const entitiesToUpsert = entities.map(entity => ({
    ...entity,
    deleted: null,
    modified: new Date().toISOString()
  }));

  const { data, error } = await supabase
    .from("analysis_entity")
    .upsert(
      entitiesToUpsert,
      {
        onConflict: "tenant_id,email",
        ignoreDuplicates: false
      }
    )
    .select();

  if (error) {
    throw error;
  }
  return (data || []) as AnalysisEntity[];
};

// Analysis Profiles
// NOTE(types): same status narrowing as AnalysisEntity above.
export const fetchAnalysisProfiles = async (): Promise<AnalysisProfile[]> => {
  const { data, error } = await supabase
    .from("analysis_profile")
    .select("*")
    .is("deleted", null)
    .order("created", { ascending: false });

  if (error) {
    throw error;
  }
  return (data || []) as AnalysisProfile[];
};

export const createAnalysisProfile = async (
  profile: Omit<AnalysisProfile, "id" | "created" | "modified" | "deleted">
): Promise<AnalysisProfile> => {
  const { data, error } = await supabase
    .from("analysis_profile")
    .insert(profile)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as AnalysisProfile;
};

export const updateAnalysisProfile = async (
  id: string,
  updates: Partial<AnalysisProfile>
): Promise<AnalysisProfile> => {
  const { data, error } = await supabase
    .from("analysis_profile")
    .update({ ...updates, modified: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as AnalysisProfile;
};

export const deleteAnalysisProfile = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from("analysis_profile")
    .update({ deleted: new Date().toISOString() })
    .eq("id", id);

  if (error) {
    throw error;
  }
};

// Account Analysis Configuration
// NOTE(types): DB stores analysis_scope/timezone/locale as generic `string` and
// pipeline_sync_status as `Json`; we narrow here to the app-level typed interface.
export const fetchAccountAnalysisConfiguration = async (): Promise<
  AccountAnalysisConfiguration | null
> => {
  const { data, error } = await supabase
    .from("account_analysis_configuration")
    .select("*")
    .limit(1)
    .single();

  if (error && error.code !== "PGRST116") {
    throw error;
  } // PGRST116 is "no rows returned"
  return (data || null) as unknown as AccountAnalysisConfiguration | null;
};

export const createAccountAnalysisConfiguration = async (
  config: Omit<AccountAnalysisConfiguration, "id" | "created" | "modified">
): Promise<AccountAnalysisConfiguration> => {
  const { data, error } = await supabase
    .from("account_analysis_configuration")
    // NOTE(types): app-level `analysis_scope`/`timezone`/`locale` unions and `pipeline_sync_status` shape are narrower than the generated Insert type (which widens them to `string`/`Json`).
    .insert(config as never)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as unknown as AccountAnalysisConfiguration;
};

export const updateAccountAnalysisConfiguration = async (
  id: string,
  updates: Partial<AccountAnalysisConfiguration>
): Promise<AccountAnalysisConfiguration> => {
  const { data, error } = await supabase
    .from("account_analysis_configuration")
    // NOTE(types): same narrowing mismatch as `createAccountAnalysisConfiguration` — app unions vs generated Update type.
    .update({ ...updates, modified: new Date().toISOString() } as never)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as unknown as AccountAnalysisConfiguration;
};

export const invokeSyncPipelineConfig = async (
  tenantId: string
): Promise<unknown> => {
  const { data, error } = await supabase.functions.invoke(
    "sync-pipeline-config",
    {
      body: { tenant_id: tenantId }
    }
  );

  if (error) {
    throw error;
  }
  return data;
};
